/**
 * Server-sent events, by the book: frames separated by a blank line, `event:` and
 * `data:` lines within one. A partial frame stays buffered until the chunk that
 * completes it arrives — the one detail a naive split on `\n\n` per chunk gets wrong.
 *
 * WHY IT LIVES HERE. This reader was private to `@ui/composer/suggest`, which had
 * itself replaced a second identical copy the Helper's client was carrying. A third
 * caller (the public keyword audit) made the choice between importing a suggestion
 * module for its frame parser or copying the loop again — so it moved to the transport
 * package instead, where `transport.ts` already says streaming belongs.
 */

/** `onFrame` returning true stops the read. */
export async function readFrames(
  body: ReadableStream<Uint8Array>,
  onFrame: (event: string, data: string) => boolean,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let event = 'message';
        let data = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trim();
        }
        if (!data) continue;
        if (onFrame(event, data)) return;
      }
    }
  } finally {
    // Releases the socket when we stop early or `onFrame` throws. A no-op on a stream
    // that already ended, and its rejection is not the caller's problem.
    void reader.cancel().catch(() => {});
  }
}
